import AddressAutocomplete from "features/shared/components/addressAutocomplete/AddressAutocomplete";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import { AddressEvidenceNumberType } from "features/shared/enums/addressEvidenceNumberType";
import AddressFindEnumDTO from "features/shared/enums/dynamic/AddressFindEnumDTO";
import FormGroup from "features/shared/forms/components/FormGroup";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import { Nullable } from "features/shared/types/nullable";
import React, { useEffect } from "react";
import { getFormAddress } from "./addressSubFormHelper";
import CadastralInfoSubForm from "./CadastralInfoSubForm";
import { Country } from "features/shared/enums/country";
import { useTranslation } from "react-i18next";

type Props = {
  formHandler: UseFormHookResult;
  objectPrefix: string;
  enableAutocomplete?: boolean;
  hasCadastralInfo?: boolean;
  disabled?: boolean;
};

const AddressSubForm = ({
  formHandler,
  objectPrefix,
  enableAutocomplete = false,
  hasCadastralInfo = false,
  disabled = false
}: Props) => {
  //#region translations
  const tLabels = useTranslation("contracting\\contracting").t;
  //#endregion translations

  //#region  form hook props
  const {
    bindField,
    bindCheckField,
    setFormValue,
    getFormValue,
    clearFormValues,
    clearValidationErrors,
    errors,
  } = {
    ...formHandler,
  };
  //#region  form hook props

  //#region  side-effects
  // set address defaults
  useEffect(() => {
    assignDefaultCountryCode();
    assignDefaultEvidenceNumberType();
  }, []);

  useEffect(() => {
    if (hasCadastralInfo === false) {
      assignDefaultEvidenceNumberType();  
    }
  }, [hasCadastralInfo]);

  // set default country code for manually entered address
  useEffect(() => {
    assignDefaultCountryCode();
  }, [getFormValue<boolean>(`${objectPrefix}.manuallyEntered`)]);
  
  //#region  side-effects

  //#region helpers
  const assignDefaultEvidenceNumberType = () => {
    const numberType = getFormValue<AddressEvidenceNumberType>(
      getInputName("evidenceNumberType")
    );
    if (!numberType) {
      setNumberType(AddressEvidenceNumberType.EvidenceNumber);
    }
  };

  const assignDefaultCountryCode = () => {
    const countryCode = getFormValue<string>(getInputName("countryCode"));
    if (!countryCode) {
      setFormValue(getInputName("countryCode"), Country.CZ); // TODO: add to configuraction
    }
  };

  const getInputName = (name: string) => {
    return `${objectPrefix}.${name}`;
  };

  const getNumberType = (): AddressEvidenceNumberType => {
    const numberType = getFormValue<AddressEvidenceNumberType>(
      getInputName("evidenceNumberType")
    );
    return numberType ?? AddressEvidenceNumberType.EvidenceNumber;
  };

  const setNumberType = (numberType: AddressEvidenceNumberType) => {
    setFormValue(getInputName("evidenceNumberType"), numberType);
  };

  const isVisibleFullForm = () => {
    const manuallyEnteredAddress = getFormValue<boolean>(
      getInputName("manuallyEntered")
    );

    if (enableAutocomplete && manuallyEnteredAddress !== true) {
      return false;
    }

    return true;
  };

  const getAddressToAutocomplete = (): Nullable<AddressFindEnumDTO> => {
    const address = getFormValue<AddressFindEnumDTO>(objectPrefix);

    // valid address must be filled evidence number, city and zip code
    if (address?.evidenceNumber && address?.city && address?.zipCode) {
      return address;
    }

    return null;
  };

  const clearAdressErrors = () => {
    clearValidationErrors([
      getInputName("evidenceNumber"),
      getInputName("city"),
      getInputName("zipCode"),
    ]);
  };
  //#region helpers

  //#region handlers
  const changeNumberTypeHandler = (numberType: AddressEvidenceNumberType) => {
    document.body.click(); // simulate click outside of tooltip
    setNumberType(numberType);
  };

  const addressChangeHandler = (address: Nullable<AddressFindEnumDTO>) => {
    if (address !== null) {
      setFormValue(objectPrefix, getFormAddress(address));
      clearAdressErrors();
    } else {
      clearFormValues([objectPrefix]);
    }
  };
  //#endregion handlers

  //#region sub-renders
  const renderEvidenceOrLandRegistryNumber = () => {
    let inputName: string = getInputName("evidenceNumber");

    let label: string;
    let placeholder: string;
    let tooltip: JSX.Element;

    if (getNumberType() === AddressEvidenceNumberType.LandRegistryNumber) {
      label = tLabels("addressSubForm.landRegistryNumber.label");
      placeholder = tLabels("addressSubForm.landRegistryNumber.placeholder");
      tooltip = renderAddressNumberTooltip(
        tLabels("addressSubForm.landRegistryNumber.tooltip"),
        AddressEvidenceNumberType.EvidenceNumber
      );
    } else {
      label = tLabels("addressSubForm.evidenceNumber.label");
      placeholder = tLabels("addressSubForm.evidenceNumber.placeholder");
      tooltip = renderAddressNumberTooltip(
        tLabels("addressSubForm.evidenceNumber.tooltip"),
        AddressEvidenceNumberType.LandRegistryNumber
      );
    }

    return (
      <FormGroup
        labelText={label}
        inputId={inputName}
        validationError={errors[inputName]}
        tooltip={{
          id: `${inputName}-tooltip`,
          content: tooltip,
          placement: "top",
          trigger: "click",
        }}
      >
        <input
          id={inputName}
          className="form-control"
          placeholder={placeholder}
          {...bindField(inputName)}
          disabled={disabled}
        />
      </FormGroup>
    );
  };

  const renderAddressNumberTooltip = (
    text: string,
    numberType: AddressEvidenceNumberType
  ) => {
    return (
      <span>
        {text}{" "}
        <span
          className="text-primary"
          role="button"
          onClick={disabled ? undefined : () => changeNumberTypeHandler(numberType)}
        >
          {tLabels("addressSubForm.addressNumberTooltipHere")}
        </span>
      </span>
    );
  };

  const renderFullAdressSwitch = () => {
    return (
      <div className="col-md-6 px-2 align-self-center">
        <div className="custom-control custom-checkbox mt-md-3 mb-3 mb-md-0">
          <input
            id={getInputName("manuallyEntered")}
            type="checkbox"
            className="custom-control-input"
            {...bindCheckField(getInputName("manuallyEntered"))}
            disabled={disabled}
          />
          <label
            className="custom-control-label"
            htmlFor={getInputName("manuallyEntered")}
          >
            {tLabels("addressSubForm.manuallyEntered.label")}
          </label>
        </div>
      </div>
    );
  };
  //#endregion sub-renders

  //#region render
  return (
    <div className="row no-gutters mx-n2">
      {/* Standard Address */}
      <RenderWrapper isVisible={() => !hasCadastralInfo}>
        {/* Address autocomplete */}
        <RenderWrapper isVisible={() => enableAutocomplete}>
          <div className="col-md-6 px-2">
            <FormGroup
              labelText={"Adresa"}
              validationError={errors[getInputName("zipCode")]}
            >
              <AddressAutocomplete
                onChange={addressChangeHandler}
                value={getAddressToAutocomplete()}
                disabled={disabled ? disabled : isVisibleFullForm()}
              />
            </FormGroup>
          </div>
          {renderFullAdressSwitch()}
        </RenderWrapper>

        {/* Fulll adress form */}
        <RenderWrapper isVisible={isVisibleFullForm}>
          <div className="col-lg-6 px-2">
            <FormGroup
              labelText={tLabels("addressSubForm.street.label")}
              inputId={getInputName("street")}
              validationError={errors[getInputName("street")]}
            >
              <input
                id={getInputName("street")}
                className="form-control"
                placeholder={tLabels("addressSubForm.street.placeholder")}
                {...bindField(getInputName("street"))}
                disabled={disabled}
              />
            </FormGroup>
          </div>
          <div className="col-sm-6 col-lg-3 px-2">
            {renderEvidenceOrLandRegistryNumber()}
          </div>
          <div className="col-sm-6 col-lg-3 px-2">
            <FormGroup
              labelText={tLabels("addressSubForm.houseNumber.label")}
              inputId={getInputName("houseNumber")}
              validationError={errors[getInputName("houseNumber")]}
            >
              <input
                id={getInputName("houseNumber")}
                className="form-control"
                placeholder={tLabels("addressSubForm.houseNumber.placeholder")}
                {...bindField(getInputName("houseNumber"))}
                disabled={disabled}
              />
            </FormGroup>
          </div>
          <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("addressSubForm.city.label")}
              inputId={getInputName("city")}
              validationError={errors[getInputName("city")]}
            >
              <input
                id={getInputName("city")}
                className="form-control"
                placeholder={tLabels("addressSubForm.city.placeholder")}
                {...bindField(getInputName("city"))}
                disabled={disabled}
              />
            </FormGroup>
          </div>
          <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("addressSubForm.cityPart.label")}
              inputId={getInputName("cityPart")}
              validationError={errors[getInputName("cityPart")]}
            >
              <input
                id={getInputName("cityPart")}
                className="form-control"
                placeholder={tLabels("addressSubForm.cityPart.placeholder")}
                {...bindField(getInputName("cityPart"))}
                disabled={disabled}
              />
            </FormGroup>
          </div>
          <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("addressSubForm.zipCode.label")}
              inputId={getInputName("zipCode")}
              validationError={errors[getInputName("zipCode")]}
            >
              <input
                id={getInputName("zipCode")}
                className="form-control"
                placeholder={tLabels("addressSubForm.zipCode.placeholder")}
                {...bindField(getInputName("zipCode"))}
                disabled={disabled}
              />
            </FormGroup>
          </div>
        </RenderWrapper>
      </RenderWrapper>
      {/* Cadastral address */}
      <RenderWrapper isVisible={() => hasCadastralInfo}>
        <div className="col-12 px-2">
          <CadastralInfoSubForm
            formHandler={formHandler}
            objectPrefix={objectPrefix}
          />
        </div>
      </RenderWrapper>
    </div>
  );
  //#endregion render
};

export default AddressSubForm;
